import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiService, SRIngestFailure } from '../services/apiService';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { SRErrorDisplay, toSrErrorDisplay } from '../utils/srErrors';
import { ActionMenu, ActionMenuOption } from './ActionMenu';
import { Button } from './Button';
import { Modal } from './Modal';
import { SRErrorNotice } from './SRErrorNotice';
import { Spinner } from './Spinner';

interface SRFailedIngestModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function formatOccurredAt(isoString: string | null): string {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatPatient(row: SRIngestFailure): string {
  const parts = [row.patient_given_name, row.patient_family_name].filter(Boolean);
  if (parts.length === 0) return '—';
  const name = parts.join(' ');
  return row.patient_nhi ? `${name} (${row.patient_nhi})` : name;
}

function formatFailureType(type: string): string {
  return type.replace(/_/g, ' ');
}

const ERROR_PREVIEW_MAX_LEN = 120;

function getErrorPreview(message: string): { text: string; truncated: boolean } {
  const normalized = message.trim();
  if (normalized.length <= ERROR_PREVIEW_MAX_LEN) {
    return { text: normalized, truncated: false };
  }
  return {
    text: `${normalized.slice(0, ERROR_PREVIEW_MAX_LEN).trimEnd()}…`,
    truncated: true,
  };
}

const errorCellButtonStyle: React.CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  width: '100%',
  padding: 0,
  border: 'none',
  background: 'none',
  textAlign: 'left',
  font: 'inherit',
  color: 'inherit',
  cursor: 'pointer',
  wordBreak: 'break-word',
};

const errorCellButtonTruncatedStyle: React.CSSProperties = {
  ...errorCellButtonStyle,
  color: '#2563eb',
  textDecoration: 'underline',
  textDecorationStyle: 'dotted',
  textUnderlineOffset: '2px',
};

export const SRFailedIngestModal: React.FC<SRFailedIngestModalProps> = ({
  isOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const { user: currentUser } = useCurrentUser();
  const userRole = currentUser?.role?.toLowerCase() || '';
  const canQueueManualTriage = userRole === 'admin' || userRole === 'manager';
  const canRetryIngest = userRole === 'admin';

  const [failures, setFailures] = useState<SRIngestFailure[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<SRErrorDisplay | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [copiedPostId, setCopiedPostId] = useState<string | null>(null);
  const [queueingId, setQueueingId] = useState<string | null>(null);
  const [retryingPostId, setRetryingPostId] = useState<string | null>(null);
  const [queueMessage, setQueueMessage] = useState<string | null>(null);
  const [selectedError, setSelectedError] = useState<string | null>(null);

  const loadFailures = useCallback(async (targetPage: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await ApiService.getSRIngestFailures(targetPage, 25);
      setFailures(data.failures);
      setPage(data.pagination.current_page);
      setTotalPages(data.pagination.total_pages);
      setTotalCount(data.summary.total_open_failures);
    } catch (err) {
      setError(
        toSrErrorDisplay(err, {
          code: 'SR-050',
          message: 'Unable to load failed ingestions. Please try again.',
        }),
      );
      setFailures([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadFailures(1);
    }
  }, [isOpen, loadFailures]);

  const handleCopyPostId = async (postId: string) => {
    try {
      await navigator.clipboard.writeText(postId);
      setCopiedPostId(postId);
      window.setTimeout(() => setCopiedPostId(null), 2000);
    } catch {
      setCopiedPostId(null);
    }
  };

  const handleQueueManualTriage = async (srReferralId: string) => {
    setQueueingId(srReferralId);
    setQueueMessage(null);
    setError(null);
    try {
      const result = await ApiService.queueSrManualTriage(srReferralId);
      setQueueMessage(
        result.already_queued
          ? 'Referral is already queued for manual triage.'
          : 'Queued for manual triage. Open AI Triaging to complete fields.',
      );
      await loadFailures(page);
    } catch (err) {
      setError(
        toSrErrorDisplay(err, {
          code: 'SR-053',
          message: 'Unable to queue referral for manual triage.',
        }),
      );
    } finally {
      setQueueingId(null);
    }
  };

  const handleRetryIngest = async (postId: string) => {
    setRetryingPostId(postId);
    setQueueMessage(null);
    setError(null);
    try {
      const result = await ApiService.retrySRIngest(postId);
      if (result.success) {
        setQueueMessage('Ingest replayed successfully.');
        await loadFailures(page);
      } else {
        setError(
          toSrErrorDisplay(new Error('Ingest retry did not complete successfully'), {
            code: 'SR-054',
            message: 'Ingest retry did not complete successfully.',
          }),
        );
      }
    } catch (err) {
      setError(
        toSrErrorDisplay(err, {
          code: 'SR-054',
          message: 'Unable to retry ingest.',
        }),
      );
    } finally {
      setRetryingPostId(null);
    }
  };

  const buildRowActions = (row: SRIngestFailure): ActionMenuOption[] => {
    const options: ActionMenuOption[] = [];

    if (row.post_id) {
      options.push({
        label: copiedPostId === row.post_id ? 'Copied post_id' : 'Copy post_id',
        onClick: () => handleCopyPostId(row.post_id!),
      });
    }

    if (row.sr_referral_id) {
      options.push({
        label: 'View',
        onClick: () => {
          onClose();
          navigate(`/admin/sr-queue?referralId=${row.sr_referral_id}`);
        },
      });
    }

    if (
      canQueueManualTriage &&
      row.failure_type === 'triage_failed' &&
      row.sr_referral_id
    ) {
      options.push({
        label: 'Queue for manual triage',
        onClick: () => handleQueueManualTriage(row.sr_referral_id!),
        isLoading: queueingId === row.sr_referral_id,
        variant: 'primary',
      });
    }

    if (canRetryIngest && row.failure_type === 'ingest_failed' && row.post_id) {
      options.push({
        label: 'Retry ingest',
        onClick: () => handleRetryIngest(row.post_id!),
        isLoading: retryingPostId === row.post_id,
        variant: 'primary',
      });
    }

    return options;
  };

  if (!isOpen) return null;

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isLoading={loading}
      className="sr-failed-ingest-modal"
      style={{ maxWidth: '1200px' }}
    >
      <div className="modal-header">
        <h3>Failed ingestions</h3>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onClose}
          style={{ marginLeft: 'auto' }}
        >
          Close
        </button>
      </div>

      <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        {error && (
          <div style={{ marginBottom: '12px' }}>
            <SRErrorNotice code={error.code} message={error.message} />
          </div>
        )}

        {queueMessage && (
          <div
            style={{
              marginBottom: '12px',
              padding: '8px 12px',
              backgroundColor: '#ecfdf5',
              border: '1px solid #6ee7b7',
              borderRadius: '6px',
              fontSize: '0.875rem',
              color: '#065f46',
            }}
          >
            {queueMessage}
          </div>
        )}

        {loading && failures.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
            <Spinner />
          </div>
        ) : failures.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#6b7280' }}>No failed ingestions</p>
        ) : (
          <table
            className="table"
            style={{ width: '100%', fontSize: '0.875rem', tableLayout: 'fixed' }}
          >
            <thead>
              <tr>
                <th>When</th>
                <th>SR number</th>
                <th>Type</th>
                <th>Patient</th>
                <th>GP</th>
                <th>Error</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {failures.map((row, index) => (
                <tr key={`${row.post_id || row.sr_referral_id || index}`}>
                  <td>{formatOccurredAt(row.occurred_at)}</td>
                  <td>{row.display_referral_no || '—'}</td>
                  <td>{formatFailureType(row.failure_type)}</td>
                  <td>{formatPatient(row)}</td>
                  <td>
                    {[row.gp_name, row.gp_practice].filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td style={{ width: '28%', verticalAlign: 'top' }}>
                    {row.error_message?.trim() ? (() => {
                      const { text, truncated } = getErrorPreview(row.error_message);
                      return (
                        <button
                          type="button"
                          style={truncated ? errorCellButtonTruncatedStyle : errorCellButtonStyle}
                          title={truncated ? 'View full error' : undefined}
                          onClick={() => setSelectedError(row.error_message!.trim())}
                        >
                          {text}
                        </button>
                      );
                    })() : (
                      '—'
                    )}
                  </td>
                  <td style={{ width: '48px', textAlign: 'right' }}>
                    {(() => {
                      const options = buildRowActions(row);
                      if (options.length === 0) {
                        return '—';
                      }
                      return (
                        <ActionMenu
                          icon="more-horizontal"
                          options={options}
                          isLoading={
                            (queueingId != null && queueingId === row.sr_referral_id) ||
                            (retryingPostId != null && retryingPostId === row.post_id)
                          }
                        />
                      );
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div
        className="modal-footer"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
          {totalCount} open failure{totalCount === 1 ? '' : 's'}
        </span>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            variant="secondary"
            size="sm"
            disabled={loading || page <= 1}
            onClick={() => loadFailures(page - 1)}
          >
            Previous
          </Button>
          <span style={{ alignSelf: 'center', fontSize: '0.85rem' }}>
            Page {page} of {totalPages}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={loading || page >= totalPages}
            onClick={() => loadFailures(page + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </Modal>

    {selectedError && (
      <Modal
        isOpen
        onClose={() => setSelectedError(null)}
        style={{ maxWidth: '640px' }}
      >
        <div className="modal-header">
          <h3>Error details</h3>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setSelectedError(null)}
            style={{ marginLeft: 'auto' }}
          >
            Close
          </button>
        </div>
        <div
          className="modal-body"
          style={{
            maxHeight: '50vh',
            overflowY: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: '0.875rem',
            lineHeight: 1.5,
          }}
        >
          {selectedError}
        </div>
      </Modal>
    )}
    </>
  );
};
